import { ArrowBack } from "@mui/icons-material";
import { Alert, Button, Container, Grid, InputAdornment, TextField, Typography } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setEventData } from "../../redux/slices/createEventSlice";
import { fetchEvent, resetUploadReqStatus, selectEditEvent, selectEditEventFetchStatus, selectEditEventStatus, uploadEditEvent } from "../../redux/slices/editEventSlice";
import { fetchEvents } from "../../redux/slices/eventsSlice";
import {
    addEventTickets,
    deleteEventTicketsThunk,
    fetchEventTickets,
    resetEventTicketsMutateStatus,
    selectEventTicketsError,
    selectEventTicketsMutateStatus,
    updateEventTicketsThunk,
} from "../../redux/slices/eventTicketsSlice";
import { fetchPromoters, selectPromoters } from "../../redux/slices/promotersSlice";
import { AppDispatch } from "../../redux/store/store";
import { getGeoCodeFromAdress } from "../../util/firebaseUtil";
import { Ticket } from "../../util/types";
import { convertEventDataToLocal, getTicketArray, isTicketPriceConfigurationValid, ticketRequiresFreeLimit } from "../../util/utils";
import LoadingView from "../general/LoadingView";
import InputFileUpload from "./InputFileUpload";

export default function MasterEditEventForm({ clubId, eventId, setSelectedEventId }: { clubId: string, eventId: string, setSelectedEventId: (t: string | null) => void }) {
    const dispatch: AppDispatch = useDispatch();
    const reqStatusUpload = useSelector(selectEditEventStatus);
    const reqStatusFetch = useSelector(selectEditEventFetchStatus);
    const event = useSelector(selectEditEvent);
    const eventTicketsMutateStatus = useSelector(selectEventTicketsMutateStatus);
    const eventTicketsReqError = useSelector(selectEventTicketsError);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [imgUrl, setimgUrl] = useState<string | null>(null);
    const [validationErrorMsg, setValidationErrorMsg] = useState("");
    const [checked, setChecked] = useState<string[]>([]);
    const promoters = useSelector(selectPromoters);
    const [tickets, setTickets] = useState<Ticket[]>([]);
    // EventTickets: saved via eventTicketsSlice -> services/eventTickets
    const [eventTickets, setEventTickets] = useState<Ticket[]>([]);
    const [eventTicketsAction, setEventTicketsAction] = useState<"save" | "delete" | null>(null);
    const [eventTicketsFlash, setEventTicketsFlash] = useState<{ severity: "success" | "error"; message: string } | null>(null);

    const eventTicketsIsMutating = eventTicketsMutateStatus === "pending";

    const initialEventData = {
        ticketPrice: '',
        endTime: '',
        eventName: '',
        eventLocation: '',
        eventDescription: '',
        eventVenueName: '',
        startTime: '',
        age: '',
        genre: '',
        eventId: '',
        videoUrl: '',
        paymentLink: ''
    };
    const [localEventData, setLocalEventData] = useState(initialEventData);

    useEffect(() => {
        async function fetchData() {
            if (eventId) {
                //get selected event
                dispatch(fetchEvent({ eventId: eventId }))
                dispatch(fetchPromoters())

                // fetch eventTickets from dedicated endpoint
                try {
                    const res = await dispatch(fetchEventTickets({ eventId, includeFees: false })).unwrap();
                    setEventTickets((res as any)?.tickets ?? []);
                } catch (e) {
                    // ignore; UI can still work with empty list
                    setEventTickets([]);
                }
            }
        }
        fetchData();
    }, [eventId])

    useEffect(() => {
        if (eventTicketsMutateStatus === "succeeded") {
            const msg = eventTicketsAction === "delete" ? "Event Ticket gelöscht." : "Event Tickets gespeichert.";
            setEventTicketsFlash({ severity: "success", message: msg });

            const timeout = window.setTimeout(() => {
                setEventTicketsFlash(null);
                setEventTicketsAction(null);
                dispatch(resetEventTicketsMutateStatus());
            }, 2000);

            return () => window.clearTimeout(timeout);
        }

        if (eventTicketsMutateStatus === "failed") {
            setEventTicketsFlash({
                severity: "error",
                message: eventTicketsReqError ?? "Event Tickets konnten nicht gespeichert werden.",
            });
        }
    }, [dispatch, eventTicketsAction, eventTicketsMutateStatus, eventTicketsReqError]);

    useEffect(() => {
        async function fetchData() {
            if (event) {
                const eventDataLoaded = await convertEventDataToLocal(event);
                setimgUrl(event.eventImageCardUrl ?? event.eventImage ?? null);
                setLocalEventData(eventDataLoaded);
                // Populate tickets state from event
                setTickets(event.tickets && event.tickets.length > 0 ? event.tickets : []);
                // eventTickets are fetched separately via fetchEventTickets
            }
        }
        fetchData();
    }, [event])

    useEffect(() => {
        if (promoters) {
            const filteredPromoters = promoters?.filter(promoter => promoter?.events?.includes(eventId ?? "")).map(x => x.uid) ?? [];
            setChecked(filteredPromoters);
        }
    }, [promoters])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setLocalEventData({ ...localEventData, [name]: value });
    };

    const handleDateTimeChange = (date: Dayjs | null, fieldName: string) => {
        // Convert the date to a string
        const dateString = date && date.isValid() ? date.toDate().toISOString() : '';

        // Update the state with the string date
        setLocalEventData({ ...localEventData, [fieldName]: dateString });
    };

    const handleAddTicket = () => {
        setTickets([
            ...tickets,
            {
                ticketTypeName: "",
                ticketTypePrice: 0,
                ticketsAvailable: 0,
                freeTicketsPerUserLimit: undefined,
                ticketTypeDescription: "",
                ticketBuyableUntil: "",
            },
        ]);
    };

    const handleTicketChange = (index: number, field: keyof Ticket, value: string) => {
        const updatedTickets = [...tickets];
        const existing = updatedTickets[index] ? { ...updatedTickets[index] } : {} as Ticket;
        const newValue: any = (field === "ticketTypePrice" || field === "ticketsAvailable" || field === "freeTicketsPerUserLimit")
            ? (value === "" ? undefined : Number(value))
            : value;
        (existing as any)[field] = newValue;
        updatedTickets[index] = existing;
        setTickets(updatedTickets);
    };

    const handleTicketDateChange = (index: number, date: Dayjs | null) => {
        const updatedTickets = [...tickets];
        const existing = updatedTickets[index] ? { ...updatedTickets[index] } : {} as Ticket;
        existing.ticketBuyableUntil = date && date.isValid() ? date.toDate().toISOString() : "";
        updatedTickets[index] = existing;
        setTickets(updatedTickets);
    };

    const handleRemoveTicket = (index: number) => {
        setTickets(tickets.filter((_, i) => i !== index));
    };

    const handleAddEventTicket = () => {
        const baseStartTime = localEventData.startTime || event?.startTime;
        const defaultTicketBuyableUntil = baseStartTime && dayjs(baseStartTime).isValid()
            ? dayjs(baseStartTime).subtract(2, "hour").toDate().toISOString()
            : "";

        setEventTickets([
            ...eventTickets,
            {
                ticketTypeName: "",
                ticketTypePrice: 0,
                ticketsAvailable: 0,
                freeTicketsPerUserLimit: undefined,
                ticketTypeDescription: "",
                ticketBuyableUntil: defaultTicketBuyableUntil,
            },
        ]);
    };

    const handleEventTicketChange = (index: number, field: keyof Ticket, value: string) => {
        const updated = [...eventTickets];
        const existing = updated[index] ? { ...updated[index] } : ({} as Ticket);
        const newValue: any = (field === "ticketTypePrice" || field === "ticketsAvailable" || field === "freeTicketsPerUserLimit")
            ? (value === "" ? undefined : Number(value))
            : value;
        (existing as any)[field] = newValue;
        updated[index] = existing;
        setEventTickets(updated);
    };

    const handleEventTicketDateChange = (index: number, date: Dayjs | null) => {
        const updated = [...eventTickets];
        const existing = updated[index] ? { ...updated[index] } : ({} as Ticket);
        existing.ticketBuyableUntil = date && date.isValid() ? date.toDate().toISOString() : "";
        updated[index] = existing;
        setEventTickets(updated);
    };

    const handleRemoveEventTicket = (index: number) => {
        const ticketToRemove = eventTickets[index];
        setEventTickets(eventTickets.filter((_, i) => i !== index));

        // If ticket exists on backend, delete it there too
        if (ticketToRemove?.eventTicketId) {
            setEventTicketsAction("delete");
            dispatch(deleteEventTicketsThunk({
                eventId: eventId ?? "",
                tickets: [ticketToRemove] as any,
                includeFees: false,
            }))
                .unwrap()
                .then((res) => setEventTickets((res as any)?.tickets ?? []))
                .catch(() => {
                    // best-effort; keep local UI change
                });
        }
    };


    async function handleSubmit() {
        setValidationErrorMsg("");
        if (await validateInputsAndShowError()) {
            const { lat, lng } = await getGeoCodeFromAdress(localEventData.eventLocation);
            if (lat && lng) {
                dispatch(uploadEditEvent({
                    eventData: {
                        ...event,
                        ...localEventData,
                        clubId: clubId ?? "",
                        eventLocation: { latitude: lat, longitude: lng },
                        eventAddress: localEventData.eventLocation,
                        eventImage: imgUrl ?? "",
                        tickets: ((tickets.length > 0 ? tickets : (localEventData.ticketPrice ? getTicketArray(event?.tickets, parseInt(localEventData.ticketPrice)) : event?.tickets || [])) as Ticket[])
                            .map(t => ({ ...t, ticketBuyableUntil: t?.ticketBuyableUntil ? new Date(t.ticketBuyableUntil).toISOString() : t.ticketBuyableUntil })),
                        promoters: checked
                    },
                    file: selectedFile
                }));
                dispatch(setEventData(localEventData));
            } else {
                setValidationErrorMsg("Die angegebene Adresse ist falsch!")
            }
        }
    };

    async function handleSubmitEventTicket() {
        setValidationErrorMsg("");

        const paymentLink = String((localEventData as any)?.paymentLink ?? "").trim();
        if (paymentLink) {
            setValidationErrorMsg("Payment Link darf nicht gesetzt sein, wenn Event Tickets existieren.");
            return;
        }

        const valid = eventTickets.every((t) =>
            t.ticketTypeName &&
            isTicketPriceConfigurationValid(t) &&
            (t.ticketsAvailable !== undefined && t.ticketsAvailable >= 0) &&
            (typeof t.ticketBuyableUntil === "string" && t.ticketBuyableUntil !== "")
        );
        if (!valid) {
            setValidationErrorMsg("Bitte fülle alle Event-Ticket-Felder aus. Kostenlose normale Tickets brauchen ein Limit von mindestens 1 pro User, bezahlte Tickets einen Preis größer als 0 (Menge ≥ 0, Verkauf bis).");
            return;
        }

        const normalizeTickets = (list: Ticket[]) =>
            list.map((t) => ({
                ...t,
                ticketBuyableUntil: typeof t.ticketBuyableUntil === "string" ? t.ticketBuyableUntil : "",
            }));

        const newTickets = normalizeTickets(eventTickets.filter((t) => !t.eventTicketId));
        const existingTickets = normalizeTickets(eventTickets.filter((t) => Boolean(t.eventTicketId)));

        try {
            setEventTicketsAction("save");
            let latestTickets: Ticket[] = eventTickets;
            if (newTickets.length > 0) {
                const res = await dispatch(addEventTickets({ eventId: eventId ?? "", tickets: newTickets as any, includeFees: false })).unwrap();
                latestTickets = (res as any)?.tickets ?? latestTickets;
            }
            if (existingTickets.length > 0) {
                const res = await dispatch(updateEventTicketsThunk({ eventId: eventId ?? "", tickets: existingTickets as any, includeFees: false })).unwrap();
                latestTickets = (res as any)?.tickets ?? latestTickets;
            }
            setEventTickets(latestTickets);
        } catch (e) {
            setValidationErrorMsg("Event Tickets konnten nicht gespeichert werden.");
        }
    };

    async function validateInputsAndShowError() {
        const chain = localEventData.startTime &&
            localEventData.endTime &&
            localEventData.eventName &&
            localEventData.eventDescription &&
            localEventData.eventLocation &&
            (selectedFile || imgUrl);

        // if tickets exist, validate them too
        const ticketsOk = tickets.length === 0 || tickets.every(ticket =>
            ticket.ticketTypeName &&
            isTicketPriceConfigurationValid(ticket) &&
            (ticket.ticketsAvailable !== undefined && ticket.ticketsAvailable >= 0) &&
            (typeof ticket.ticketBuyableUntil === "string" && ticket.ticketBuyableUntil !== "")
        );
        if (!(chain && ticketsOk)) {
            const errorList: string[] = [];
            if (!localEventData.startTime) {
                errorList.push("Startzeit");
            }
            if (!localEventData.endTime) {
                errorList.push("Endzeit");
            }
            if (!localEventData.eventName) {
                errorList.push("Event Name");
            }
            if (!localEventData.eventDescription) {
                errorList.push("Beschreibung");
            }
            if (!localEventData.eventLocation) {
                errorList.push("Event Adresse");
            }
            if (!selectedFile && !imgUrl) {
                errorList.push("Bilddatei");
            }
            if (tickets.some(ticket =>
                !ticket.ticketTypeName ||
                !isTicketPriceConfigurationValid(ticket) ||
                ticket.ticketsAvailable === undefined || ticket.ticketsAvailable === null || Number.isNaN(ticket.ticketsAvailable) ||
                ticket.ticketsAvailable < 0)) {
                errorList.push("Alle Ticketfelder müssen ausgefüllt sein. Kostenlose normale Tickets brauchen ein Limit von mindestens 1 pro User, bezahlte Tickets einen Preis größer als 0 (Menge: 0 oder größer).");
            }
            if (tickets.some(ticket => !ticket.ticketBuyableUntil || ticket.ticketBuyableUntil === "")) {
                errorList.push("Bitte geben Sie für jedes Ticket an, bis wann es gekauft werden kann (Verkauf bis).");
            }
            if (errorList.length > 0) {
                const errorMessage = `Die folgenden Felder sind nicht ausgefüllt, werden jedoch benötigt: ${errorList.join(', ')}`;
                setValidationErrorMsg(errorMessage);
                console.error(errorMessage);
            }
        }
        return Boolean(chain && ticketsOk);
    }

    if (reqStatusFetch === "pending" || reqStatusUpload === "pending") {
        return <LoadingView />
    }


    return (
        <Container maxWidth="md" sx={{ marginTop: 3, marginBottom: 10 }}>
            <Button
                variant="contained"
                startIcon={<ArrowBack />}
                sx={{ marginTop: 2, marginBottom: 4 }}
                onClick={() => {
                    //refetch all events (edited events too)
                    dispatch(fetchEvents({ clubId: clubId ?? "", forceRefresh: true }))
                    setSelectedEventId(null)
                    //remove alert
                    dispatch(resetUploadReqStatus())
                }}
            >
                Back
            </Button>
            {validationErrorMsg && <Alert severity="error">{validationErrorMsg}</Alert>}
            {reqStatusUpload === "succeeded" && <Alert severity="success">{"Event erfolgreich gespeichert!"}</Alert>}
            <Typography variant="h4" gutterBottom>
                Add Event
            </Typography>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <InputFileUpload
                            selectedFile={selectedFile}
                            setSelectedFile={setSelectedFile}
                            url={event?.eventImageCardUrl ?? event?.eventImage}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <DateTimePicker
                            label="Start"
                            name="startTime"
                            ampm={false}
                            value={localEventData.startTime ? dayjs(localEventData.startTime) : null}
                            onChange={(date) => handleDateTimeChange(date, 'startTime')}
                        //renderInput={(props) => <TextField {...props} />}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <DateTimePicker
                            label="Ende"
                            name="endTime"
                            ampm={false}
                            value={localEventData.endTime ? dayjs(localEventData.endTime) : null}
                            onChange={(date) => handleDateTimeChange(date, 'endTime')}
                        //renderInput={(props) => <TextField {...props} />}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            required
                            label="Event Name"
                            name="eventName"
                            value={localEventData.eventName}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Mindestalter"
                            name="age"
                            value={localEventData.age}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Adresse"
                            name="eventLocation"
                            value={localEventData.eventLocation}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Name des Veranstaltungsortes"
                            name="eventVenueName"
                            value={localEventData.eventVenueName}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Genre"
                            name="genre"
                            value={localEventData.genre}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Ticketpreis"
                            name="ticketPrice"
                            value={localEventData.ticketPrice}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                            Caution: Editing Tickets ONLY for Guestlist right now!
                        </Typography>
                        <Typography variant="h6" gutterBottom>
                            Tickets
                        </Typography>
                    </Grid>

                    {tickets.map((ticket, index) => (
                        <Fragment key={index}>
                            <Grid item xs={3}>
                                <TextField
                                    label="Ticketname"
                                    value={ticket.ticketTypeName}
                                    onChange={(e) => handleTicketChange(index, "ticketTypeName", e.target.value)}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={2} >
                                <TextField
                                    label="Ticketpreis"
                                    type="number"
                                    value={ticket.ticketTypePrice}
                                    onChange={(e) => handleTicketChange(index, "ticketTypePrice", e.target.value)}
                                    fullWidth
                                    required
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <TextField
                                    label="Verfügbare Tickets"
                                    type="number"
                                    value={ticket.ticketsAvailable}
                                    onChange={(e) => handleTicketChange(index, "ticketsAvailable", e.target.value)}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <TextField
                                    label="Ticketbeschreibung"
                                    value={ticket.ticketTypeDescription}
                                    onChange={(e) => handleTicketChange(index, "ticketTypeDescription", e.target.value)}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <DateTimePicker
                                    label="Verkauf bis"
                                    ampm={false}
                                    value={ticket.ticketBuyableUntil ? dayjs(ticket.ticketBuyableUntil) : null}
                                    onChange={(date) => handleTicketDateChange(index, date)}
                                />
                            </Grid>
                            <Grid item xs={1} sx={{ display: "flex", alignItems: "flex-end" }}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={handleAddTicket}
                                    sx={{ marginRight: 1 }}
                                >
                                    +
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={() => handleRemoveTicket(index)}
                                >
                                    -
                                </Button>
                            </Grid>
                            {ticketRequiresFreeLimit(ticket) && (
                                <Grid item xs={12}>
                                    <TextField
                                        label="Kostenlose Tickets pro User"
                                        type="number"
                                        value={ticket.freeTicketsPerUserLimit ?? ""}
                                        onChange={(e) => handleTicketChange(index, "freeTicketsPerUserLimit", e.target.value)}
                                        fullWidth
                                        required
                                        inputProps={{ min: 1, step: 1 }}
                                        helperText="Pflichtfeld für normale kostenlose Tickets."
                                    />
                                </Grid>
                            )}
                        </Fragment>
                    ))}

                    <Grid item xs={12} sx={{ mt: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Event Tickets
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            UI only (noch nicht gespeichert)
                        </Typography>
                    </Grid>

                    {eventTicketsAction && eventTicketsIsMutating && (
                        <Grid item xs={12} sx={{ mb: 1 }}>
                            <Alert severity="info">
                                {eventTicketsAction === "delete" ? "Lösche Event Ticket…" : "Speichere Event Tickets…"}
                            </Alert>
                        </Grid>
                    )}

                    {eventTicketsFlash && (
                        <Grid item xs={12} sx={{ mb: 1 }}>
                            <Alert severity={eventTicketsFlash.severity}>{eventTicketsFlash.message}</Alert>
                        </Grid>
                    )}

                    {eventTickets.length === 0 && (
                        <Grid item xs={12} sx={{ mb: 1 }}>
                            <Button variant="outlined" onClick={handleAddEventTicket} disabled={eventTicketsIsMutating}>
                                Add new event ticket
                            </Button>
                        </Grid>
                    )}
                    {eventTickets.length > 0 && (
                        <Grid item xs={12} sx={{ mb: 1 }}>
                            <Button variant="outlined" onClick={handleSubmitEventTicket} disabled={eventTicketsIsMutating}>
                                Save Event Tickets
                            </Button>
                        </Grid>
                    )}

                    {eventTickets.map((ticket, index) => (
                        <Fragment key={`eventTicket-${index}`}>
                            <Grid item xs={3}>
                                <TextField
                                    label="Ticketname"
                                    value={ticket.ticketTypeName}
                                    onChange={(e) => handleEventTicketChange(index, "ticketTypeName", e.target.value)}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <TextField
                                    label="Ticketpreis"
                                    type="number"
                                    value={ticket.ticketTypePrice}
                                    onChange={(e) => handleEventTicketChange(index, "ticketTypePrice", e.target.value)}
                                    fullWidth
                                    required
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">€</InputAdornment>,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <TextField
                                    label="Verfügbare Tickets"
                                    type="number"
                                    value={ticket.ticketsAvailable}
                                    onChange={(e) => handleEventTicketChange(index, "ticketsAvailable", e.target.value)}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <TextField
                                    label="Ticketbeschreibung"
                                    value={ticket.ticketTypeDescription}
                                    onChange={(e) => handleEventTicketChange(index, "ticketTypeDescription", e.target.value)}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <DateTimePicker
                                    label="Verkauf bis"
                                    ampm={false}
                                    value={ticket.ticketBuyableUntil ? dayjs(ticket.ticketBuyableUntil) : null}
                                    onChange={(date) => handleEventTicketDateChange(index, date)}
                                />
                            </Grid>
                            <Grid item xs={1} sx={{ display: "flex", alignItems: "flex-end" }}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={handleAddEventTicket}
                                    sx={{ marginRight: 1 }}
                                    disabled={eventTicketsIsMutating}
                                >
                                    +
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={() => handleRemoveEventTicket(index)}
                                    disabled={eventTicketsIsMutating}
                                >
                                    -
                                </Button>
                            </Grid>
                            {ticketRequiresFreeLimit(ticket) && (
                                <Grid item xs={12}>
                                    <TextField
                                        label="Kostenlose Tickets pro User"
                                        type="number"
                                        value={ticket.freeTicketsPerUserLimit ?? ""}
                                        onChange={(e) => handleEventTicketChange(index, "freeTicketsPerUserLimit", e.target.value)}
                                        fullWidth
                                        required
                                        inputProps={{ min: 1, step: 1 }}
                                        helperText="Pflichtfeld für normale kostenlose Tickets."
                                    />
                                </Grid>
                            )}
                        </Fragment>
                    ))}
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="VideoUrl"
                            name="videoUrl"
                            value={localEventData.videoUrl}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Payment Link"
                            name="paymentLink"
                            value={(localEventData as any).paymentLink}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Beschreibung"
                            name="eventDescription"
                            value={localEventData.eventDescription}
                            multiline
                            rows={4}
                            onChange={handleChange}
                        />
                    </Grid>
                    {/* <Grid item xs={12}>
                        <ScrollableCheckboxList
                            clubPromoters={promoters ?? []}
                            setChecked={setChecked}
                            checked={checked} />
                    </Grid> */}
                    <Grid item xs={12}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit}
                        >
                            Save
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </Container>
    )
}
